import { encode as cborEncode } from 'cbor-x';
import {
  isWebHidSupported,
  openFido2Device,
  closeFido2Device,
} from './ctap2-hid';
import {
  authenticatorGetInfo,
  authenticatorMakeCredential,
  setPin,
  getPinToken,
  computeMakeCredentialAuth,
} from './ctap2-commands';

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * Shape of the Graph beta creationOptions(challengeTimeoutInMinutes=N) response.
 *
 * The publicKey field is structurally a PublicKeyCredentialCreationOptionsJSON,
 * except excludeCredentials IDs carry a trailing padding-count digit (0, 1, or 2)
 * that must be stripped before use.
 *
 * @see https://learn.microsoft.com/en-us/graph/api/fido2authenticationmethod-creationoptions
 */
export interface GraphCreationOptions {
  value: {
    publicKey: {
      challenge: string;
      timeout: number;
      attestation: string;
      rp: { id: string; name: string };
      user: { id: string; name: string; displayName: string };
      pubKeyCredParams: Array<{ type: string; alg: number }>;
      excludeCredentials?: Array<{ id: string; type: string; transports?: string[] }>;
      authenticatorSelection?: {
        authenticatorAttachment?: string;
        requireResidentKey?: boolean;
        residentKey?: string;
        userVerification?: string;
      };
      extensions?: Record<string, unknown>;
    };
    challengeTimeoutDateTime: string;
  };
}

/**
 * Result of creating a FIDO2 credential on a USB security key via CTAP2 over WebHID.
 * All binary fields are Base64URL-encoded strings ready for the Graph fido2Methods POST.
 */
export interface WebAuthnCreationResult {
  credentialId: string;
  clientDataJSON: string;
  attestationObject: string;
  authenticatorAttachment: string | null;
  clientExtensionResults: Record<string, unknown>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Fix an excludeCredentials ID from the Graph beta API.
 *
 * Graph returns Base64URL credential IDs with a trailing digit (0, 1, or 2)
 * that indicates how many base64 padding characters ('=') were originally present.
 * Since Base64URL omits padding, we simply strip the trailing digit to get a
 * valid Base64URL string.
 */
function fixExcludeCredentialId(id: string): string {
  if (id.length === 0) return id;
  const lastChar = id.charAt(id.length - 1);
  if (lastChar === '0' || lastChar === '1' || lastChar === '2') {
    return id.slice(0, -1);
  }
  return id;
}

/** Decode a Base64URL string to Uint8Array */
function base64UrlDecode(b64url: string): Uint8Array {
  // Restore padding
  let base64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4 !== 0) {
    base64 += '=';
  }
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Encode a Uint8Array as a Base64URL string (no padding) */
function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Check if the current browser supports WebHID for direct CTAP2 communication.
 * Requires Chrome or Edge 89+.
 */
export function isWebAuthnSupported(): boolean {
  return isWebHidSupported();
}

/**
 * Create a FIDO2 credential on a USB security key using CTAP2 over WebHID.
 *
 * This bypasses the browser's WebAuthn API entirely, communicating directly
 * with the security key via CTAP2. This allows credential creation for any
 * RP ID (including login.microsoft.com) regardless of the page's origin.
 *
 * Flow:
 * 1. Open a WebHID connection to the FIDO2 security key
 * 2. Check authenticator info (PIN support, etc.)
 * 3. Set/verify PIN on the security key
 * 4. Construct clientDataJSON manually
 * 5. Send authenticatorMakeCredential via CTAP2
 * 6. Return attestation response for Graph API registration
 *
 * @param options - The creationOptions response from the Graph beta API
 * @param pin - The PIN to set on / use with the security key
 * @returns Attestation result with Base64URL-encoded credential fields
 */
export async function createFido2Credential(
  options: GraphCreationOptions,
  pin: string
): Promise<WebAuthnCreationResult> {
  const { publicKey } = options.value;

  // Step 1: Open WebHID connection to the security key
  const conn = await openFido2Device();

  try {
    // Step 2: Get authenticator info
    const info = await authenticatorGetInfo(conn);
    console.debug('[CTAP2] Authenticator info:', {
      versions: info.versions,
      aaguid: base64UrlEncode(info.aaguid),
      options: info.options,
      pinProtocols: info.pinUvAuthProtocols,
    });

    const hasPinSupport = info.options?.clientPin !== undefined;
    const pinIsSet = info.options?.clientPin === true;

    // Step 3: Handle PIN
    let pinToken: Uint8Array | undefined;

    if (hasPinSupport) {
      if (!pinIsSet) {
        // No PIN set yet — set it now
        console.debug('[CTAP2] Setting PIN on authenticator');
        await setPin(conn, pin);
      }

      // Get a PIN token for authenticating the MakeCredential command
      console.debug('[CTAP2] Getting PIN token');
      pinToken = await getPinToken(conn, pin);
    }

    // Step 4: Construct clientDataJSON
    // We build this ourselves since the browser normally constructs it.
    // The origin must be a plausible HTTPS origin — Microsoft validates this
    // against the RP ID. Use the RP ID as the origin.
    const clientData = {
      type: 'webauthn.create',
      challenge: publicKey.challenge,
      origin: `https://${publicKey.rp.id}`,
      crossOrigin: false,
    };
    const clientDataJSON = new TextEncoder().encode(JSON.stringify(clientData));
    const clientDataHash = new Uint8Array(
      await crypto.subtle.digest('SHA-256', clientDataJSON)
    );

    // Step 5: Build excludeList from Graph options
    const excludeList = publicKey.excludeCredentials?.map((cred) => ({
      type: cred.type,
      id: base64UrlDecode(fixExcludeCredentialId(cred.id)),
    }));

    // Step 6: Compute pinUvAuthParam if we have a PIN token
    let pinUvAuthParam: Uint8Array | undefined;
    if (pinToken) {
      pinUvAuthParam = await computeMakeCredentialAuth(pinToken, clientDataHash);
    }

    // Step 7: Call authenticatorMakeCredential via CTAP2
    console.debug('[CTAP2] Sending MakeCredential command — touch your security key');
    const result = await authenticatorMakeCredential(conn, {
      clientDataHash,
      rp: publicKey.rp,
      user: {
        id: base64UrlDecode(publicKey.user.id),
        name: publicKey.user.name,
        displayName: publicKey.user.displayName,
      },
      pubKeyCredParams: publicKey.pubKeyCredParams,
      excludeList,
      extensions: publicKey.extensions,
      options: {
        rk: publicKey.authenticatorSelection?.residentKey === 'required' ||
            publicKey.authenticatorSelection?.requireResidentKey === true,
        uv: false, // UV handled via PIN token
      },
      pinUvAuthParam,
      pinUvAuthProtocol: pinToken ? 1 : undefined,
    });

    // Step 8: Build the attestation object (CBOR: { fmt, attStmt, authData })
    const attestationObject = cborEncode(new Map<string, unknown>([
      ['fmt', result.fmt],
      ['attStmt', result.attStmt],
      ['authData', result.authData],
    ]));

    // Step 9: Extract credential ID from authData
    // authData layout: rpIdHash(32) + flags(1) + signCount(4) + attestedCredData(...)
    // attestedCredData: aaguid(16) + credIdLen(2) + credentialId(credIdLen) + ...
    const credIdLen = (result.authData[53] << 8) | result.authData[54];
    const credentialId = result.authData.slice(55, 55 + credIdLen);

    return {
      credentialId: base64UrlEncode(credentialId),
      clientDataJSON: base64UrlEncode(clientDataJSON),
      attestationObject: base64UrlEncode(new Uint8Array(attestationObject)),
      authenticatorAttachment: 'cross-platform',
      clientExtensionResults: {},
    };
  } finally {
    await closeFido2Device(conn);
  }
}

